import { isNavigationFailure, NavigationFailureType } from 'vue-router';

import { isLazyImportFailure, isUnreportableChunkFailure } from '@/helpers/retry-lazy-import.js';

// Vue Router 4.4 does not expose NavigationFailureType.redirected; redirects use
// ErrorTypes.NAVIGATION_GUARD_REDIRECT (2), which is not a public runtime export.
const NAVIGATION_GUARD_REDIRECT = 2;
const CHUNK_RELOAD_KEY = 'luna:lazy-import-reloaded';

// Survives only for this document. Used when sessionStorage is blocked, and to debounce
// synchronous double-calls before navigation tears the page down.
let reloadStartedInThisDocument = false;

function readPersistedReloadMarker() {
  try {
    return sessionStorage.getItem(CHUNK_RELOAD_KEY) !== null;
  } catch {
    return false;
  }
}

/**
 * @returns {boolean} false when the marker could not be persisted — caller must not reload,
 * otherwise a storage-less tab would reload in an unbounded loop.
 */
function persistReloadMarker() {
  try {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
    return true;
  } catch {
    return false;
  }
}

/** @returns {void} */
export function resetLazyImportReloadGuardForTests() {
  reloadStartedInThisDocument = false;
}

/**
 * If a page still cannot download after retrying, reload it at most once per tab session.
 * @returns {boolean} true only when a reload started. If we already used that reload,
 * return false so boot can show a fallback instead of leaving the page blank.
 */
export function reloadOnLazyImportFailure(error, reload = () => window.location.reload()) {
  if (!isLazyImportFailure(error) || isUnreportableChunkFailure(error)) {
    return false;
  }

  if (reloadStartedInThisDocument || readPersistedReloadMarker()) {
    return false;
  }

  reloadStartedInThisDocument = true;
  if (!persistReloadMarker()) {
    // sessionStorage unavailable: skipping reload avoids an infinite reload loop across loads.
    return false;
  }

  reload();
  return true;
}

// A failed navigation leaves the URL on the page the user came from, so a plain reload would drop
// them back there instead of on the page they asked for.
function reloadToDestination(destination) {
  return destination ? () => window.location.assign(destination) : () => window.location.reload();
}

function resolveDestinationHref(router, location) {
  try {
    return router.resolve(location).href;
  } catch {
    return undefined;
  }
}

/**
 * If the user opens Page A, then chooses Page B, a late download failure from A must not
 * send them back to A. Each push or replace gets a number so we can recognize old requests.
 * The old retries still run, but their download failures cannot trigger a reload.
 * The newest request keeps its normal recovery, even while it is still loading.
 */
export function installRouterErrorRecovery(router) {
  let latestNavigation = 0;

  function wrapMethod(methodName) {
    const originalMethod = router[methodName];
    router[methodName] = async function wrappedRouterMethod(...args) {
      latestNavigation += 1;
      const navigation = latestNavigation;
      try {
        return await originalMethod.apply(this, args);
      } catch (error) {
        if (navigation !== latestNavigation && isLazyImportFailure(error)) {
          return;
        }
        onError(error, undefined, resolveDestinationHref(router, args[0]));
      }
    };
  }

  wrapMethod('push');
  wrapMethod('replace');
}

/**
 * Ignore expected navigation failures. If a page download used up its retries, try one reload.
 * @param {unknown} error
 * @param {() => void} [reload] overrides the reload, mainly for tests
 * @param {string} [destination] URL the failed navigation was heading to; the reload lands there
 */
export function onError(error, reload, destination) {
  if (
    isNavigationFailure(error, NAVIGATION_GUARD_REDIRECT) ||
    isNavigationFailure(error, NavigationFailureType.duplicated) ||
    isNavigationFailure(error, NavigationFailureType.cancelled)
  ) {
    return;
  }
  // The page download has already used up its retries by the time its failure reaches here.
  if (isUnreportableChunkFailure(error)) {
    return;
  }
  if (
    isLazyImportFailure(error) &&
    reloadOnLazyImportFailure(error, reload ?? reloadToDestination(destination))
  ) {
    return;
  }
  throw error;
}
